import { useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchFastApi, ApiError } from "../api/fastapi";
import {
  ParsedFilePreviewSchema,
  SampleUploadResultSchema,
  type ParsedFilePreview,
  type ColumnSelection,
  type ColumnAttributeMapping,
  type SampleUploadResult,
} from "../models/audit/sampleUpload";

interface UseSampleUploadOptions {
  sessionId: string;
}

export function useSampleUpload({ sessionId }: UseSampleUploadOptions) {
  const [loading, setLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const parseFile = useCallback(
    async (file: File): Promise<ParsedFilePreview | null> => {
      setParseLoading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetchFastApi({
          path: `/matching/${sessionId}/sample-upload/parse`,
          method: "POST",
          body: formData,
        });

        const parsed = ParsedFilePreviewSchema.parse(response);
        return parsed;
      } catch (error) {
        console.error("Error parsing file:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to parse file");
        }
        return null;
      } finally {
        setParseLoading(false);
      }
    },
    [sessionId],
  );

  const confirmFirstUpload = useCallback(
    async (
      fileId: string,
      columnSelections: ColumnSelection[],
    ): Promise<SampleUploadResult | null> => {
      setConfirmLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/matching/${sessionId}/sample-upload/confirm`,
          method: "POST",
          body: {
            file_id: fileId,
            column_selections: columnSelections,
          },
        });

        const parsed = SampleUploadResultSchema.parse(response);
        if (parsed.status === "success" || parsed.status === "completed") {
          toast.success(
            `Created ${parsed.createdSampleCount} samples with ${parsed.createdAttributeCount} attributes`,
          );
        } else if (parsed.status === "partial") {
          toast.warning(parsed.message ?? "Some samples could not be created");
        }
        return parsed;
      } catch (error) {
        console.error("Error confirming upload:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to create samples");
        }
        return null;
      } finally {
        setConfirmLoading(false);
      }
    },
    [sessionId],
  );

  const confirmSubsequentUpload = useCallback(
    async (
      fileId: string,
      columnMappings: ColumnAttributeMapping[],
    ): Promise<SampleUploadResult | null> => {
      setConfirmLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/matching/${sessionId}/sample-upload/confirm`,
          method: "POST",
          body: {
            file_id: fileId,
            column_mappings: columnMappings,
          },
        });

        const parsed = SampleUploadResultSchema.parse(response);
        if (parsed.status === "success" || parsed.status === "completed") {
          toast.success(`Created ${parsed.createdSampleCount} samples`);
        } else if (parsed.status === "partial") {
          toast.warning(parsed.message ?? "Some samples could not be created");
        }
        return parsed;
      } catch (error) {
        console.error("Error confirming upload:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to create samples");
        }
        return null;
      } finally {
        setConfirmLoading(false);
      }
    },
    [sessionId],
  );

  const getPreview = useCallback(
    async (fileId: string): Promise<ParsedFilePreview | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/matching/${sessionId}/sample-upload/preview/${fileId}`,
          method: "GET",
        });

        const parsed = ParsedFilePreviewSchema.parse(response);
        return parsed;
      } catch (error) {
        console.error("Error getting preview:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to get preview");
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [sessionId],
  );

  return {
    loading,
    parseLoading,
    confirmLoading,
    parseFile,
    confirmFirstUpload,
    confirmSubsequentUpload,
    getPreview,
  };
}
